/**
 * DTOs de clientes OAuth de un módulo (`/modules/:code/clients`). Ver docs/FASE-0.md §5
 * "Clientes OAuth".
 */
import { z } from "zod";
import { PATTERNS } from "../constants.js";
export const OAuthClientTypeSchema = z.enum(["public", "confidential"]);
/** Sin valores explícitos en FASE-0 §3; `active`/`revoked` es la decisión mínima razonable (igual que `packages/db`). */
export const OAuthClientStatusSchema = z.enum(["active", "revoked"]);
/** Identificador público de cliente OAuth: minúsculas, dígitos, `-`/`_`, 3-64 caracteres. */
const CLIENT_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
/**
 * Corrección de un cliente ya registrado. Todos los campos son opcionales y hace falta al menos
 * uno: es la operación que faltaba para arreglar un redirect URI mal escrito o una lista de scopes
 * incompleta sin tener que registrar otro cliente con otro `clientId`.
 *
 * No se pueden cambiar dos cosas, y no por descuido:
 *
 * - `clientId` es la identidad del cliente; cambiarlo es registrar otro.
 * - `clientType` decide si el cliente tiene secreto. Pasar de confidencial a público dejaría un
 *   secreto huérfano, y al revés dejaría un cliente confidencial sin secreto que nadie puede usar;
 *   en ambos casos lo correcto es registrar el cliente que se quería.
 *
 * `status` sí se puede mover en los dos sentidos: `revoked` retira un cliente del flujo OIDC —el
 * núcleo ya comprueba el estado en `/oauth/authorize` y en `/oauth/token`— y es la forma
 * reversible y auditable de retirar uno, en vez de un borrado que arrastraría por cascada sus
 * refresh tokens y sus códigos de autorización, dejando sin sesión a todo el mundo.
 */
export const UpdateOAuthClientInputSchema = z
    .object({
    allowedScopes: z.array(z.string().min(1)).min(1).optional(),
    postLogoutRedirectUris: z.array(z.url()).optional(),
    redirectUris: z.array(z.url()).optional(),
    status: OAuthClientStatusSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, "Hay que cambiar al menos un campo.");
export const CreateOAuthClientInputSchema = z.object({
    clientId: z.string().regex(CLIENT_ID_PATTERN),
    clientType: OAuthClientTypeSchema,
    redirectUris: z.array(z.url()).default([]),
    postLogoutRedirectUris: z.array(z.url()).default([]),
    allowedScopes: z.array(z.string().min(1)).min(1),
});
/** Read-model de un cliente OAuth, sin el secreto (nunca se devuelve tras la creación). */
export const OAuthClientSchema = z.object({
    clientId: z.string(),
    moduleCode: z.string().regex(PATTERNS.MODULE_CODE),
    clientType: OAuthClientTypeSchema,
    redirectUris: z.array(z.string()),
    postLogoutRedirectUris: z.array(z.string()),
    allowedScopes: z.array(z.string()),
    status: OAuthClientStatusSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Respuesta de `POST /modules/:code/clients` y de `rotate-secret`: lleva `clientSecret` en
 * claro una única vez. `null` para clientes públicos (sin secreto).
 */
export const OAuthClientCreatedSchema = OAuthClientSchema.extend({
    clientSecret: z.string().nullable(),
});
//# sourceMappingURL=clients.js.map